import { db } from '../../config/firebase/config';
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { syncMesaFromPOS, markBillPaidInPOS } from '../firebase/functions';
import { POSAdapterFactory } from './POSAdapterFactory';
import { POSAdapter } from './POSAdapter';
import { POSConfig, POSOrder, POSItem, POSSyncResponse } from './types';

/**
 * Main POS service - used by components to interact with POS systems
 */
export class POSService {
  private adapter: POSAdapter;
  private restaurantId: string;

  constructor(restaurantId: string, posConfig: POSConfig) {
    this.restaurantId = restaurantId;
    this.adapter = POSAdapterFactory.createAdapter(posConfig);
  }

  /**
   * Test connection to POS
   */
  async testConnection(): Promise<boolean> {
    return await this.adapter.testConnection();
  }

  /**
   * Sync a specific mesa from POS via Cloud Function
   * Uses server-to-server API calls for security
   */
  async syncMesa(mesaId: string): Promise<POSSyncResponse> {
    try {
      console.log(`🔄 Syncing mesa ${mesaId} via Cloud Function...`);

      const result = await syncMesaFromPOS({ mesaId });
      const data = result.data as any;
      
      if (data.success) {
        console.log(`✅ Mesa ${mesaId} synced successfully`);
        return {
          success: true,
          data: {
            mesaId: data.mesaId,
            itemCount: data.itemCount,
            total: data.total
          },
          timestamp: new Date()
        };
      } else {
        return {
          success: false,
          error: data.error || 'Sync failed',
          timestamp: new Date()
        };
      }
    } catch (error: any) {
      console.error('Error syncing mesa:', error);
      return {
        success: false,
        error: error.message || 'Unknown error',
        timestamp: new Date()
      };
    }
  }

  /**
   * Sync all open mesas
   */
  async syncAllOpenMesas(mesaIds: string[]): Promise<POSSyncResponse[]> {
    const results = await Promise.all(
      mesaIds.map(mesaId => this.syncMesa(mesaId))
    );
    return results;
  }

  /**
   * Mark bill as paid in POS (via Cloud Function)
   */
  async markBillPaid(mesaId: string, paymentMethod: string, amountPaid: number): Promise<POSSyncResponse> {
    try {
      const result = await markBillPaidInPOS({ mesaId, paymentMethod, amountPaid });
      const data = result.data as any;
      
      return {
        success: true,
        data,
        timestamp: new Date()
      };
    } catch (error: any) {
      console.error('Error marking bill as paid:', error);
      return {
        success: false,
        error: error.message,
        timestamp: new Date()
      };
    }
  }

  /**
   * Get provider name
   */
  getProvider(): string {
    return this.adapter.getProvider();
  }
}

/**
 * Create a POS service instance for a restaurant
 */
export function createPOSService(
  restaurantId: string,
  posConfig: POSConfig
): POSService {
  return new POSService(restaurantId, posConfig);
}
